import {ref, readonly} from "vue";

const RELOAD_GUARD_KEY = "app-version-reload";

const APP_NAME = process.env.VUE_APP_NAME;
const PKG_VERSION = process.env.VUE_APP_PKG_VERSION;
const GIT_HASH = process.env.VUE_APP_GIT_HASH;
const CURRENT_VERSION = process.env.VUE_APP_VERSION;
const BUILD_TIME = process.env.VUE_APP_BUILD_TIME;

const updateAvailable = ref(false);
const serverVersion = ref(null);

async function fetchServerVersion() {
    try {
        const resp = await fetch(`${process.env.BASE_URL}version.json?t=${Date.now()}`, {cache: "no-store"});
        if (!resp.ok) return null;
        const data = await resp.json();
        return data?.version ?? null;
        // eslint-disable-next-line no-unused-vars
    } catch (ignored) {
        return null; // offline, server down, etc.
    }
}

async function checkForUpdate() {
    const version = await fetchServerVersion();
    if (version && version !== CURRENT_VERSION) {
        serverVersion.value = version;
        updateAvailable.value = true;
    }
    return updateAvailable.value;
}

function hardReload(path) {
    // avoid infinite reload loops (e.g. misconfigured cache)
    const lastTarget = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (lastTarget === `${CURRENT_VERSION}|${path}`) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, `${CURRENT_VERSION}|${path}`);

    const url = new URL(path || window.location.href, window.location.origin);
    url.searchParams.set("_r", Date.now().toString());

    window.location.replace(url.toString());
    return true;
}

function isChunkLoadError(error) {
    const msg = `${error?.name} ${error?.message}`;
    return /ChunkLoadError|Loading chunk .* failed|Loading CSS chunk|Failed to fetch dynamically imported module/i.test(
        msg
    );
}

export default {
    install(app, {router} = {}) {
        app.provide("appVersion", {
            name: APP_NAME,
            pkgVersion: PKG_VERSION,
            gitHash: GIT_HASH,
            version: CURRENT_VERSION,
            buildTime: BUILD_TIME,
            serverVersion: readonly(serverVersion),
            updateAvailable: readonly(updateAvailable),
            checkForUpdate,
            reload: () => hardReload(null)
        });

        console.info(`[app] version ${CURRENT_VERSION} (built ${BUILD_TIME})`);

        if (process.env.NODE_ENV !== "production") return;

        if (router) {
            // old chunk files removed by the new deployment -> reload
            router.onError((error, to) => {
                if (isChunkLoadError(error)) hardReload(to?.fullPath);
            });

            // navigation succeeded -> reset the reload-loop guard
            router.afterEach(() => sessionStorage.removeItem(RELOAD_GUARD_KEY));
        }
    }
};
